import React, { useMemo, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import Form from "../../components/form/Form";
import DocumentPreview from "../../components/document/DocumentPreview";
import CandidateService from "../../services/candidateService";
import { getErrorToastMessage } from "../../utils/httpToast";

const EditCandidatePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const candidateService = useMemo(() => CandidateService(), []);

  const [candidate, setCandidate] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedDoc, setSelectedDoc] = useState(null);

  const returnUrl = useMemo(() => {
    const sp = new URLSearchParams(location.search);
    const raw = sp.get("returnUrl");
    return raw ? decodeURIComponent(raw) : "/candidates";
  }, [location.search])

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      try {
        setLoading(true);

        const data = await candidateService.get(id, { signal: controller.signal });

        if (!data) {
          throw new Error("Candidate not found or empty response");
        }

        setCandidate(data);
      } catch (err) {
        if (err.name === 'CanceledError') return;
        navigate(returnUrl, {
          state: {
            toast: {
              type: "error",
              message: getErrorToastMessage(err, t),
            },
          },
          replace: true,
        });
      } finally {
        setLoading(false);
      }
    };

    load();
    return () => controller.abort();
  }, [id, navigate, t, candidateService, returnUrl]);

  useEffect(() => {
    if (candidate?.candidateDocuments?.length > 0) {
      setSelectedDoc(candidate.candidateDocuments[0]);
    } else {
      setSelectedDoc(null);
    }
  }, [candidate]);

  const goCandidatesWithToast = (type, message) => {
    navigate(returnUrl, { state: { toast: { type, message } } });
  };

  const handleSaveSuccess = () => {
    goCandidatesWithToast("success", t("MODAL.SUCCESSFUL"));
  };

  const handleSaveError = (err) => {
    goCandidatesWithToast("error", getErrorToastMessage(err, t));
  };

  const handleCancel = () => {
    navigate(returnUrl);
  };

  if (loading) {
    return <div className="flex justify-center py-10"><p>{t("ACTIONS.LOADING_MORE")}</p></div>;
  }
  if (!candidate) {
    return <div className="flex justify-center py-10"><p className="text-red font-semibold">{t("CANDIDATES.NOT_FOUND")}</p></div>;
  }

  return (
    <div className="min-h-screen bg-lightGray py-4 lg:py-8 px-4 flex justify-center">
      <div className="w-full max-w-2xl lg:max-w-7xl flex flex-col lg:flex-row gap-4 lg:items-stretch">
        <div className="w-full lg:w-1/2 bg-white rounded-xl shadow-sm p-8 h-fit">
          <Form
            initialData={candidate}
            isEditing={true}
            onDocumentSelect={setSelectedDoc}
            activeDocId={selectedDoc?.id}
            onSaveSuccess={handleSaveSuccess}
            onSaveError={handleSaveError}
            onCancel={handleCancel}
          />
        </div>

        <DocumentPreview selectedDoc={selectedDoc} />
      </div>
    </div>
  );
};

export default EditCandidatePage;